import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Snackbar,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    DeleteOutline,
    ExpandLess,
    ExpandMore,
    LockOpenOutlined,
    LockOutlined,
    Save,
} from '@mui/icons-material';
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { ProgramTypeEnum } from '../../../Enum/ProgramTypeEnum';
import {
    IDeletedOnboarderGoal,
    IOnboarderGoalAllocationDto,
    IOnboarderProvider,
    IOnboardingGoalsDto,
    IOnboardingProgramGoalView,
} from '../../../Models/AdminOnboarding/OnboarderGoal.model';
import {
    copyPreviousMonthGoals,
    getOnboarderProviders,
    getOnboardingGoals,
    updateOnboardingGoals,
} from './OnboardingGoalService';

interface NotificationState {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
}

interface ProgramDefinition {
    ProgramType: ProgramTypeEnum;
    ProgramName: string;
    Description: string;
}

const programDefinitions: ProgramDefinition[] = [
    {
        ProgramType: ProgramTypeEnum.FullProgram,
        ProgramName: 'Full Program',
        Description: '12-week coaching track',
    },
    {
        ProgramType: ProgramTypeEnum.FUHFUM,
        ProgramName: 'FUHFUM',
        Description: 'Follow-up after hospitalization',
    },
    {
        ProgramType: ProgramTypeEnum.HRA,
        ProgramName: 'HRA',
        Description: 'Health risk assessment',
    },
    {
        ProgramType: ProgramTypeEnum.andNext,
        ProgramName: 'andNext',
        Description: 'Metabolic program',
    },
];

const getProviderName = (
    provider?: IOnboarderProvider
): string => {
    if (!provider) {
        return '';
    }

    return `${provider.FirstName ?? ''} ${
        provider.LastName ?? ''
    }`.trim();
};

const getProviderInitials = (
    provider?: IOnboarderProvider
): string => {
    if (!provider) {
        return '';
    }

    return `${provider.FirstName?.charAt(0) ?? ''}${
        provider.LastName?.charAt(0) ?? ''
    }`.toUpperCase();
};

const getPreviousMonth = (year: number, month: number) => {
    const date = new Date(year, month - 2, 1);

    return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
    };
};

const getNextMonth = (year: number, month: number) => {
    const date = new Date(year, month, 1);

    return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
    };
};

const OnboardingGoals = () => {
    const currentDate = new Date();

    const [selectedYear, setSelectedYear] = useState<number>(
        currentDate.getFullYear()
    );
    const [selectedMonth, setSelectedMonth] = useState<number>(
        currentDate.getMonth() + 1
    );

    const [providers, setProviders] = useState<
        IOnboarderProvider[]
    >([]);

    const [programs, setPrograms] = useState<
        IOnboardingProgramGoalView[]
    >([]);

    const [deletedGoals, setDeletedGoals] = useState<
        IDeletedOnboarderGoal[]
    >([]);

    const [viewTab, setViewTab] = useState<number>(0);
    const [isLoading, setIsLoading] =
        useState<boolean>(false);
    const [isSaving, setIsSaving] =
        useState<boolean>(false);
    const [hasChanges, setHasChanges] =
        useState<boolean>(false);

    const [notification, setNotification] =
        useState<NotificationState>({
            open: false,
            message: '',
            severity: 'success',
        });

    const showNotification = useCallback(
        (
            message: string,
            severity: NotificationState['severity']
        ) => {
            setNotification({
                open: true,
                message,
                severity,
            });
        },
        []
    );

    const buildPrograms = useCallback(
        (response: IOnboardingGoalsDto) => {
            const apiPrograms = response?.ProgramGoals ?? [];

            const result: IOnboardingProgramGoalView[] =
                programDefinitions.map((definition) => {
                    const apiProgram = apiPrograms.find(
                        (program) =>
                            Number(program.ProgramType) ===
                            Number(definition.ProgramType)
                    );

                    return {
                        Id: apiProgram?.Id,
                        ProgramType: definition.ProgramType,
                        ProgramName: definition.ProgramName,
                        Description: definition.Description,
                        Target: Number(apiProgram?.Target ?? 0),
                        Goals:
                            apiProgram?.Allocations?.filter(
                                (allocation) =>
                                    !allocation.IsDeleted
                            ) ?? [],
                        IsDeleted: false,
                        IsExpanded: false,
                    };
                });

            setPrograms(result);
        },
        []
    );

    const loadProviders = useCallback(async () => {
        try {
            const providerList =
                await getOnboarderProviders(
                    'Office Onboarding',
                    false
                );

            setProviders(
                [...providerList].sort((first, second) =>
                    getProviderName(first).localeCompare(
                        getProviderName(second)
                    )
                )
            );
        } catch (error) {
            console.error(error);

            showNotification(
                'Unable to load onboarders.',
                'error'
            );
        }
    }, [showNotification]);

    const loadGoals = useCallback(async () => {
        setIsLoading(true);

        try {
            const response = await getOnboardingGoals(
                selectedYear,
                selectedMonth
            );

            buildPrograms(response);
            setDeletedGoals([]);
            setHasChanges(false);
        } catch (error) {
            console.error(error);

            buildPrograms({
                Year: selectedYear,
                Month: selectedMonth,
                ProgramGoals: [],
            });

            showNotification(
                'Unable to load onboarding goals.',
                'error'
            );
        } finally {
            setIsLoading(false);
        }
    }, [
        selectedYear,
        selectedMonth,
        buildPrograms,
        showNotification,
    ]);

    useEffect(() => {
        void loadProviders();
    }, [loadProviders]);

    useEffect(() => {
        void loadGoals();
    }, [loadGoals]);

    const monthDisplay = useMemo(
        () =>
            new Date(
                selectedYear,
                selectedMonth - 1,
                1
            ).toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric',
            }),
        [selectedYear, selectedMonth]
    );

    const previousMonthDisplay = useMemo(() => {
        const previous = getPreviousMonth(
            selectedYear,
            selectedMonth
        );

        return new Date(
            previous.year,
            previous.month - 1,
            1
        ).toLocaleDateString('en-US', {
            month: 'long',
        });
    }, [selectedYear, selectedMonth]);

    const getActiveGoals = useCallback(
        (
            program: IOnboardingProgramGoalView
        ): IOnboarderGoalAllocationDto[] =>
            program.Goals.filter((goal) => !goal.IsDeleted),
        []
    );

    const summary = useMemo(() => {
        const monthTarget = programs.reduce(
            (total, program) =>
                total + Number(program.Target || 0),
            0
        );

        const assigned = programs.reduce(
            (programTotal, program) =>
                programTotal +
                getActiveGoals(program).reduce(
                    (goalTotal, goal) =>
                        goalTotal +
                        Number(goal.Goals || 0),
                    0
                ),
            0
        );

        const needsAttention = programs.filter(
            (program) => {
                const assignedForProgram =
                    getActiveGoals(program).reduce(
                        (total, goal) =>
                            total +
                            Number(goal.Goals || 0),
                        0
                    );

                return (
                    Number(program.Target || 0) !==
                    assignedForProgram
                );
            }
        ).length;

        return {
            monthTarget,
            assigned,
            unassigned: Math.max(
                monthTarget - assigned,
                0
            ),
            needsAttention,
            onboarders: providers.length,
        };
    }, [programs, providers.length, getActiveGoals]);

    const hasDuplicateGoals = useMemo(() => {
        return programs.some((program) => {
            const providerIds = new Set<string>();

            return getActiveGoals(program).some((goal) => {
                if (providerIds.has(goal.ProviderId)) {
                    return true;
                }

                providerIds.add(goal.ProviderId);
                return false;
            });
        });
    }, [programs, getActiveGoals]);

    const hasOverAssignedGoal = useMemo(() => {
        return programs.some((program) => {
            const assigned = getActiveGoals(program).reduce(
                (total, goal) =>
                    total + Number(goal.Goals || 0),
                0
            );

            return assigned > Number(program.Target || 0);
        });
    }, [programs, getActiveGoals]);

    const updateProgram = useCallback(
        (
            programType: ProgramTypeEnum,
            updater: (
                program: IOnboardingProgramGoalView
            ) => IOnboardingProgramGoalView
        ) => {
            setPrograms((previousPrograms) =>
                previousPrograms.map((program) =>
                    program.ProgramType === programType
                        ? updater(program)
                        : program
                )
            );

            setHasChanges(true);
        },
        []
    );

    const handlePreviousMonth = () => {
        if (
            hasChanges &&
            !window.confirm(
                'You have unsaved changes. Continue without saving?'
            )
        ) {
            return;
        }

        const previous = getPreviousMonth(
            selectedYear,
            selectedMonth
        );

        setSelectedYear(previous.year);
        setSelectedMonth(previous.month);
    };

    const handleNextMonth = () => {
        if (
            hasChanges &&
            !window.confirm(
                'You have unsaved changes. Continue without saving?'
            )
        ) {
            return;
        }

        const next = getNextMonth(
            selectedYear,
            selectedMonth
        );

        setSelectedYear(next.year);
        setSelectedMonth(next.month);
    };

    const handleTargetChange = (
        programType: ProgramTypeEnum,
        value: string
    ) => {
        const target = Math.max(0, Number(value || 0));

        updateProgram(programType, (program) => ({
            ...program,
            Target: target,
        }));
    };

    const handleToggleProgram = (
        programType: ProgramTypeEnum
    ) => {
        setPrograms((previousPrograms) =>
            previousPrograms.map((program) =>
                program.ProgramType === programType
                    ? {
                          ...program,
                          IsExpanded: !program.IsExpanded,
                      }
                    : program
            )
        );
    };

    const handleExpandAll = () => {
        const shouldExpand = programs.some(
            (program) => !program.IsExpanded
        );

        setPrograms((previousPrograms) =>
            previousPrograms.map((program) => ({
                ...program,
                IsExpanded: shouldExpand,
            }))
        );
    };

    const handleProviderGoalChange = (
        programType: ProgramTypeEnum,
        providerId: string,
        value: string
    ) => {
        const goals = Math.max(0, Number(value || 0));

        updateProgram(programType, (program) => {
            const existingGoal = program.Goals.find(
                (goal) =>
                    goal.ProviderId === providerId &&
                    !goal.IsDeleted
            );

            if (existingGoal?.IsLocked) {
                return program;
            }

            if (existingGoal) {
                return {
                    ...program,
                    Goals: program.Goals.map((goal) =>
                        goal.ProviderId === providerId &&
                        !goal.IsDeleted
                            ? {
                                  ...goal,
                                  Goals: goals,
                              }
                            : goal
                    ),
                };
            }

            return {
                ...program,
                Goals: [
                    ...program.Goals,
                    {
                        Id: '',
                        ProviderId: providerId,
                        Goals: goals,
                        IsLocked: false,
                        IsDeleted: false,
                    },
                ],
            };
        });
    };

    const handleToggleLock = (
        programType: ProgramTypeEnum,
        providerId: string
    ) => {
        updateProgram(programType, (program) => ({
            ...program,
            Goals: program.Goals.map((goal) =>
                goal.ProviderId === providerId &&
                !goal.IsDeleted
                    ? {
                          ...goal,
                          IsLocked: !goal.IsLocked,
                      }
                    : goal
            ),
        }));
    };

    const handleDeleteProviderGoal = (
        programType: ProgramTypeEnum,
        providerId: string
    ) => {
        updateProgram(programType, (program) => {
            const goal = program.Goals.find(
                (item) =>
                    item.ProviderId === providerId &&
                    !item.IsDeleted
            );

            if (!goal || goal.IsLocked) {
                return program;
            }

            if (goal.Id) {
                setDeletedGoals((previous) => [
                    ...previous.filter(
                        (item) => item.Id !== goal.Id
                    ),
                    {
                        ...goal,
                        ProgramType: programType,
                        IsDeleted: true,
                    },
                ]);
            }

            return {
                ...program,
                Goals: program.Goals.filter(
                    (item) =>
                        item.ProviderId !== providerId
                ),
            };
        });
    };

    const handleSplitEvenly = (
        programType: ProgramTypeEnum
    ) => {
        if (providers.length === 0) {
            return;
        }

        updateProgram(programType, (program) => {
            const target = Number(program.Target || 0);

            const lockedTotal = providers.reduce(
                (total, provider) => {
                    const existingGoal = program.Goals.find(
                        (goal) =>
                            goal.ProviderId === provider.Id &&
                            !goal.IsDeleted
                    );

                    return existingGoal?.IsLocked
                        ? total +
                              Number(
                                  existingGoal.Goals || 0
                              )
                        : total;
                },
                0
            );

            const unlockedProviders = providers.filter(
                (provider) => {
                    const existingGoal = program.Goals.find(
                        (goal) =>
                            goal.ProviderId === provider.Id &&
                            !goal.IsDeleted
                    );

                    return !existingGoal?.IsLocked;
                }
            );

            const remainingTarget = Math.max(
                target - lockedTotal,
                0
            );

            const baseValue =
                unlockedProviders.length > 0
                    ? Math.floor(
                          remainingTarget /
                              unlockedProviders.length
                      )
                    : 0;

            let remainder =
                unlockedProviders.length > 0
                    ? remainingTarget %
                      unlockedProviders.length
                    : 0;

            const goals: IOnboarderGoalAllocationDto[] =
                providers.map((provider) => {
                    const existingGoal = program.Goals.find(
                        (goal) =>
                            goal.ProviderId === provider.Id &&
                            !goal.IsDeleted
                    );

                    if (existingGoal?.IsLocked) {
                        return {
                            ...existingGoal,
                            IsDeleted: false,
                        };
                    }

                    const providerGoals =
                        baseValue +
                        (remainder > 0 ? 1 : 0);

                    if (remainder > 0) {
                        remainder -= 1;
                    }

                    return {
                        Id: existingGoal?.Id ?? '',
                        ProviderId: provider.Id,
                        Goals: providerGoals,
                        IsLocked: false,
                        IsDeleted: false,
                    };
                });

            return {
                ...program,
                Goals: goals,
            };
        });
    };

    const handleClearProgram = (
        programType: ProgramTypeEnum
    ) => {
        updateProgram(programType, (program) => {
            const goalsToDelete = program.Goals.filter(
                (goal) =>
                    Boolean(goal.Id) &&
                    !goal.IsLocked
            );

            if (goalsToDelete.length > 0) {
                setDeletedGoals((previous) => [
                    ...previous.filter(
                        (deleted) =>
                            deleted.ProgramType !== programType
                    ),
                    ...goalsToDelete.map((goal) => ({
                        ...goal,
                        ProgramType: programType,
                        IsDeleted: true,
                    })),
                ]);
            }

            return {
                ...program,
                Goals: program.Goals.filter(
                    (goal) => goal.IsLocked
                ),
            };
        });
    };

    const handleCopyPreviousMonth = async (
        programType?: ProgramTypeEnum
    ) => {
        const previous = getPreviousMonth(
            selectedYear,
            selectedMonth
        );

        try {
            await copyPreviousMonthGoals(
                previous.year,
                previous.month,
                selectedYear,
                selectedMonth,
                programType
            );

            showNotification(
                `Goals copied from ${previousMonthDisplay}.`,
                'success'
            );

            await loadGoals();
        } catch (error) {
            console.error(error);

            showNotification(
                'Unable to copy goals from the previous month.',
                'error'
            );
        }
    };

    const handleSave = async () => {
        if (hasDuplicateGoals) {
            showNotification(
                'The same onboarder cannot be added more than once to a program.',
                'error'
            );
            return;
        }

        if (hasOverAssignedGoal) {
            showNotification(
                'Assigned goals cannot exceed the program target.',
                'error'
            );
            return;
        }

        const request: IOnboardingGoalsDto = {
            Year: selectedYear,
            Month: selectedMonth,
            ProgramGoals: programs.map((program) => ({
                Id: program.Id ?? '',
                ProgramType: program.ProgramType,
                Target: Number(program.Target || 0),
                IsDeleted: program.IsDeleted ?? false,
                Allocations: [
                    ...getActiveGoals(program).map(
                        (goal) => ({
                            Id: goal.Id ?? '',
                            ProviderId: goal.ProviderId,
                            Goals: Number(goal.Goals || 0),
                            IsLocked:
                                goal.IsLocked ?? false,
                            IsDeleted: false,
                        })
                    ),
                    ...deletedGoals
                        .filter(
                            (goal) =>
                                goal.ProgramType ===
                                program.ProgramType
                        )
                        .map((goal) => ({
                            Id: goal.Id ?? '',
                            ProviderId: goal.ProviderId,
                            Goals: Number(goal.Goals || 0),
                            IsLocked:
                                goal.IsLocked ?? false,
                            IsDeleted: true,
                        })),
                ],
            })),
        };

        setIsSaving(true);

        try {
            await updateOnboardingGoals(request);

            showNotification(
                'Onboarding goals saved successfully.',
                'success'
            );

            await loadGoals();
        } catch (error) {
            console.error(error);

            showNotification(
                'Unable to save onboarding goals.',
                'error'
            );
        } finally {
            setIsSaving(false);
        }
    };

    const allExpanded =
        programs.length > 0 &&
        programs.every((program) => program.IsExpanded);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7f7' }}>
            <Paper
                square
                elevation={0}
                sx={{
                    px: { xs: 2, md: 4 },
                    py: 3,
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Grid
                    container
                    spacing={2}
                    alignItems="flex-end"
                >
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Typography
                            color="text.secondary"
                            sx={{ maxWidth: 560 }}
                        >
                            Give each program a target for the
                            month. Split that target across the
                            onboarders who will work it. Any
                            unassigned amount remains visible.
                        </Typography>
                    </Grid>

                    <Grid
                        size={{ xs: 12, md: 4 }}
                        sx={{
                            display: 'flex',
                            justifyContent: {
                                xs: 'flex-start',
                                md: 'flex-end',
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                overflow: 'hidden',
                                bgcolor: 'white',
                            }}
                        >
                            <IconButton
                                onClick={handlePreviousMonth}
                                sx={{ borderRadius: 0 }}
                            >
                                <ChevronLeft />
                            </IconButton>

                            <Box
                                sx={{
                                    minWidth: 170,
                                    px: 2,
                                    py: 1.25,
                                    textAlign: 'center',
                                    borderLeft: '1px solid',
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontFamily: 'monospace',
                                        fontSize: 18,
                                    }}
                                >
                                    {monthDisplay}
                                </Typography>
                            </Box>

                            <IconButton
                                onClick={handleNextMonth}
                                sx={{ borderRadius: 0 }}
                            >
                                <ChevronRight />
                            </IconButton>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Paper
                square
                elevation={0}
                sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Grid container>
                    {[
                        {
                            label: 'Month Target',
                            value: summary.monthTarget,
                        },
                        {
                            label: 'Assigned',
                            value: summary.assigned,
                        },
                        {
                            label: 'Unassigned',
                            value: summary.unassigned,
                        },
                        {
                            label: 'Needs Attention',
                            value: summary.needsAttention,
                            error: true,
                        },
                        {
                            label: 'Onboarders',
                            value: summary.onboarders,
                        },
                    ].map((item) => (
                        <Grid
                            key={item.label}
                            size={{
                                xs: 6,
                                sm: 4,
                                md: 2.4,
                            }}
                            sx={{
                                px: 4,
                                py: 2.5,
                                borderRight: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Typography
                                variant="overline"
                                sx={{
                                    color: '#607d83',
                                    letterSpacing: 2,
                                    fontWeight: 700,
                                }}
                            >
                                {item.label}
                            </Typography>

                            <Typography
                                variant="h5"
                                sx={{
                                    mt: 0.5,
                                    fontWeight: 700,
                                    color: item.error
                                        ? 'error.main'
                                        : 'text.primary',
                                }}
                            >
                                {item.value}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            <Box sx={{ p: { xs: 2, md: 4 } }}>
                {(hasDuplicateGoals ||
                    hasOverAssignedGoal) && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {hasDuplicateGoals
                            ? 'The same onboarder cannot be assigned more than once to the same program.'
                            : 'Assigned goals cannot exceed the program target.'}
                    </Alert>
                )}

                <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 2 }}
                >
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Tabs
                            value={viewTab}
                            onChange={(_, value: number) =>
                                setViewTab(value)
                            }
                            sx={{
                                minHeight: 46,
                                '& .MuiTabs-indicator': {
                                    display: 'none',
                                },
                                '& .MuiTab-root': {
                                    minHeight: 46,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    textTransform: 'none',
                                    px: 3,
                                },
                                '& .Mui-selected': {
                                    bgcolor: '#0d4d57',
                                    color: '#fff !important',
                                },
                            }}
                        >
                            <Tab label="By program" />
                            <Tab label="Grid" />
                        </Tabs>
                    </Grid>

                    <Grid
                        size={{ xs: 12, md: 6 }}
                        sx={{
                            display: 'flex',
                            justifyContent: {
                                xs: 'flex-start',
                                md: 'flex-end',
                            },
                            gap: 1,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={handleExpandAll}
                        >
                            {allExpanded
                                ? 'Collapse all'
                                : 'Expand all'}
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={
                                isSaving ? (
                                    <CircularProgress
                                        size={18}
                                        color="inherit"
                                    />
                                ) : (
                                    <Save />
                                )
                            }
                            disabled={
                                !hasChanges ||
                                isSaving ||
                                isLoading ||
                                hasDuplicateGoals ||
                                hasOverAssignedGoal
                            }
                            onClick={() => void handleSave()}
                        >
                            {isSaving
                                ? 'Saving...'
                                : 'Save'}
                        </Button>
                    </Grid>
                </Grid>

                {isLoading ? (
                    <Box
                        sx={{
                            py: 8,
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : viewTab === 0 ? (
                    <Box>
                        {programs.map((program) => {
                            const activeGoals =
                                getActiveGoals(program);

                            const assigned =
                                activeGoals.reduce(
                                    (total, goal) =>
                                        total +
                                        Number(
                                            goal.Goals || 0
                                        ),
                                    0
                                );

                            const unassigned = Math.max(
                                Number(
                                    program.Target || 0
                                ) - assigned,
                                0
                            );

                            const fullyAssigned =
                                Number(
                                    program.Target || 0
                                ) > 0 &&
                                assigned ===
                                    Number(
                                        program.Target || 0
                                    );

                            const overAssigned =
                                assigned >
                                Number(
                                    program.Target || 0
                                );

                            return (
                                <Paper
                                    key={program.ProgramType}
                                    elevation={1}
                                    sx={{
                                        mb: 2,
                                        border: '1px solid',
                                        borderColor:
                                            'divider',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box sx={{ p: 2.5 }}>
                                        <Grid
                                            container
                                            spacing={2}
                                            alignItems="center"
                                        >
                                            <Grid
                                                size={{
                                                    xs: 12,
                                                    md: 7,
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {
                                                        program.ProgramName
                                                    }
                                                </Typography>

                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {
                                                        program.Description
                                                    }
                                                </Typography>
                                            </Grid>

                                            <Grid
                                                size={{
                                                    xs: 12,
                                                    md: 5,
                                                }}
                                                sx={{
                                                    display:
                                                        'flex',
                                                    alignItems:
                                                        'center',
                                                    justifyContent:
                                                        {
                                                            xs: 'flex-start',
                                                            md: 'flex-end',
                                                        },
                                                    gap: 2,
                                                    flexWrap:
                                                        'wrap',
                                                }}
                                            >
                                                <Typography
                                                    variant="overline"
                                                    sx={{
                                                        color: '#607d83',
                                                        letterSpacing: 1.5,
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    Target
                                                </Typography>

                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={
                                                        program.Target
                                                    }
                                                    onChange={(
                                                        event
                                                    ) =>
                                                        handleTargetChange(
                                                            program.ProgramType,
                                                            event
                                                                .target
                                                                .value
                                                        )
                                                    }
                                                    slotProps={{
                                                        htmlInput:
                                                            {
                                                                min: 0,
                                                                style: {
                                                                    textAlign:
                                                                        'center',
                                                                    fontWeight: 700,
                                                                },
                                                            },
                                                    }}
                                                    sx={{
                                                        width: 85,
                                                    }}
                                                />

                                                <Box
                                                    sx={{
                                                        minWidth: 195,
                                                        px: 1.5,
                                                        py: 1,
                                                        bgcolor:
                                                            fullyAssigned
                                                                ? '#e8f3ed'
                                                                : '#edf0f0',
                                                        color:
                                                            overAssigned
                                                                ? 'error.main'
                                                                : fullyAssigned
                                                                  ? '#27734f'
                                                                  : '#40565d',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {fullyAssigned
                                                        ? '● Fully assigned'
                                                        : overAssigned
                                                          ? `● ${
                                                                assigned -
                                                                Number(
                                                                    program.Target ||
                                                                        0
                                                                )
                                                            } over assigned`
                                                          : `● ${unassigned} unassigned`}
                                                </Box>

                                                <IconButton
                                                    onClick={() =>
                                                        handleToggleProgram(
                                                            program.ProgramType
                                                        )
                                                    }
                                                >
                                                    {program.IsExpanded ? (
                                                        <ExpandLess />
                                                    ) : (
                                                        <ExpandMore />
                                                    )}
                                                </IconButton>
                                            </Grid>
                                        </Grid>

                                        <LinearProgress
                                            variant="determinate"
                                            value={
                                                Number(
                                                    program.Target ||
                                                        0
                                                ) > 0
                                                    ? Math.min(
                                                          100,
                                                          (assigned /
                                                              Number(
                                                                  program.Target
                                                              )) *
                                                              100
                                                      )
                                                    : 0
                                            }
                                            sx={{
                                                mt: 2,
                                                height: 10,
                                                bgcolor:
                                                    '#dfe5e5',
                                                '& .MuiLinearProgress-bar':
                                                    {
                                                        bgcolor:
                                                            overAssigned
                                                                ? 'error.main'
                                                                : '#17616b',
                                                    },
                                            }}
                                        />
                                    </Box>

                                    <Collapse
                                        in={program.IsExpanded}
                                    >
                                        <Divider />

                                        <Box>
                                            <Grid
                                                container
                                                sx={{
                                                    px: 3,
                                                    py: 1.5,
                                                    bgcolor:
                                                        '#fafbfb',
                                                }}
                                            >
                                                <Grid size={8}>
                                                    <Typography
                                                        variant="overline"
                                                        sx={{
                                                            color: '#607d83',
                                                            letterSpacing: 1.5,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        Onboarder
                                                    </Typography>
                                                </Grid>

                                                <Grid size={2}>
                                                    <Typography
                                                        variant="overline"
                                                        sx={{
                                                            color: '#607d83',
                                                            letterSpacing: 1.5,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        Goal
                                                    </Typography>
                                                </Grid>

                                                <Grid size={2}>
                                                    <Typography
                                                        variant="overline"
                                                        sx={{
                                                            color: '#607d83',
                                                            letterSpacing: 1.5,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        Share
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            {providers.map(
                                                (
                                                    provider,
                                                    index
                                                ) => {
                                                    const goal =
                                                        activeGoals.find(
                                                            (
                                                                item
                                                            ) =>
                                                                item.ProviderId ===
                                                                provider.Id
                                                        );

                                                    const goalValue =
                                                        goal?.Goals ??
                                                        0;

                                                    const percentage =
                                                        Number(
                                                            program.Target ||
                                                                0
                                                        ) > 0
                                                            ? Math.round(
                                                                  (goalValue /
                                                                      Number(
                                                                          program.Target
                                                                      )) *
                                                                      100
                                                              )
                                                            : 0;

                                                    return (
                                                        <Grid
                                                            key={
                                                                provider.Id
                                                            }
                                                            container
                                                            alignItems="center"
                                                            sx={{
                                                                px: 3,
                                                                py: 1.5,
                                                                borderTop:
                                                                    index ===
                                                                    0
                                                                        ? '1px solid'
                                                                        : undefined,
                                                                borderBottom:
                                                                    '1px solid',
                                                                borderColor:
                                                                    'divider',
                                                                bgcolor:
                                                                    goal?.IsLocked
                                                                        ? '#fafafa'
                                                                        : undefined,
                                                            }}
                                                        >
                                                            <Grid
                                                                size={
                                                                    8
                                                                }
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        alignItems:
                                                                            'center',
                                                                        gap: 1.5,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            width: 30,
                                                                            height: 30,
                                                                            display:
                                                                                'flex',
                                                                            alignItems:
                                                                                'center',
                                                                            justifyContent:
                                                                                'center',
                                                                            bgcolor:
                                                                                '#17616b',
                                                                            color: 'white',
                                                                            borderRadius: 0.5,
                                                                            fontSize: 12,
                                                                            fontWeight: 700,
                                                                        }}
                                                                    >
                                                                        {getProviderInitials(
                                                                            provider
                                                                        )}
                                                                    </Box>

                                                                    <Typography
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                        }}
                                                                    >
                                                                        {getProviderName(
                                                                            provider
                                                                        )}
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>

                                                            <Grid
                                                                size={
                                                                    2
                                                                }
                                                            >
                                                                <TextField
                                                                    size="small"
                                                                    type="number"
                                                                    value={
                                                                        goalValue
                                                                    }
                                                                    disabled={
                                                                        Boolean(
                                                                            goal?.IsLocked
                                                                        )
                                                                    }
                                                                    onChange={(
                                                                        event
                                                                    ) =>
                                                                        handleProviderGoalChange(
                                                                            program.ProgramType,
                                                                            provider.Id,
                                                                            event
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    slotProps={{
                                                                        htmlInput:
                                                                            {
                                                                                min: 0,
                                                                                style: {
                                                                                    textAlign:
                                                                                        'center',
                                                                                },
                                                                            },
                                                                    }}
                                                                    sx={{
                                                                        width: 110,
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid
                                                                size={
                                                                    2
                                                                }
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        alignItems:
                                                                            'center',
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            width: 115,
                                                                            height: 8,
                                                                            bgcolor:
                                                                                '#dfe5e5',
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                width: `${Math.min(
                                                                                    100,
                                                                                    percentage
                                                                                )}%`,
                                                                                height: '100%',
                                                                                bgcolor:
                                                                                    '#17616b',
                                                                            }}
                                                                        />
                                                                    </Box>

                                                                    <Typography
                                                                        color="text.secondary"
                                                                        sx={{
                                                                            minWidth: 45,
                                                                        }}
                                                                    >
                                                                        {
                                                                            percentage
                                                                        }
                                                                        %
                                                                    </Typography>

                                                                    {goal && (
                                                                        <Tooltip
                                                                            title={
                                                                                goal.IsLocked
                                                                                    ? 'Unlock allocation'
                                                                                    : 'Lock allocation'
                                                                            }
                                                                        >
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() =>
                                                                                    handleToggleLock(
                                                                                        program.ProgramType,
                                                                                        provider.Id
                                                                                    )
                                                                                }
                                                                            >
                                                                                {goal.IsLocked ? (
                                                                                    <LockOutlined
                                                                                        fontSize="small"
                                                                                        color="primary"
                                                                                    />
                                                                                ) : (
                                                                                    <LockOpenOutlined fontSize="small" />
                                                                                )}
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}

                                                                    {goal?.Id &&
                                                                        !goal.IsLocked && (
                                                                            <Tooltip title="Delete allocation">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() =>
                                                                                        handleDeleteProviderGoal(
                                                                                            program.ProgramType,
                                                                                            provider.Id
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <DeleteOutline fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    );
                                                }
                                            )}

                                            <Box
                                                sx={{
                                                    px: 3,
                                                    py: 2,
                                                    display:
                                                        'flex',
                                                    alignItems:
                                                        'center',
                                                    justifyContent:
                                                        'space-between',
                                                    gap: 2,
                                                    flexWrap:
                                                        'wrap',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display:
                                                            'flex',
                                                        gap: 2,
                                                        flexWrap:
                                                            'wrap',
                                                    }}
                                                >
                                                    <Button
                                                        size="small"
                                                        onClick={() =>
                                                            handleSplitEvenly(
                                                                program.ProgramType
                                                            )
                                                        }
                                                    >
                                                        Split evenly
                                                    </Button>

                                                    <Button
                                                        size="small"
                                                        onClick={() =>
                                                            handleClearProgram(
                                                                program.ProgramType
                                                            )
                                                        }
                                                    >
                                                        Clear
                                                    </Button>
                                                </Box>

                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {fullyAssigned
                                                        ? 'Every unit of the target has an owner.'
                                                        : `${unassigned} units remain unassigned.`}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Collapse>
                                </Paper>
                            );
                        })}
                    </Box>
                ) : (
                    <Paper
                        elevation={1}
                        sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Grid container spacing={1}>
                            <Grid size={4}>
                                <Typography fontWeight={700}>
                                    Onboarder
                                </Typography>
                            </Grid>

                            {programs.map((program) => (
                                <Grid
                                    key={program.ProgramType}
                                    size={2}
                                >
                                    <Typography
                                        fontWeight={700}
                                        textAlign="center"
                                    >
                                        {program.ProgramName}
                                    </Typography>
                                </Grid>
                            ))}

                            {providers.map((provider) => (
                                <Grid
                                    key={provider.Id}
                                    container
                                    size={12}
                                    spacing={1}
                                    alignItems="center"
                                    sx={{
                                        py: 1,
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Grid size={4}>
                                        {getProviderName(
                                            provider
                                        )}
                                    </Grid>

                                    {programs.map(
                                        (program) => {
                                            const goal =
                                                getActiveGoals(
                                                    program
                                                ).find(
                                                    (item) =>
                                                        item.ProviderId ===
                                                        provider.Id
                                                );

                                            return (
                                                <Grid
                                                    key={
                                                        program.ProgramType
                                                    }
                                                    size={2}
                                                    sx={{
                                                        display:
                                                            'flex',
                                                        alignItems:
                                                            'center',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="number"
                                                        value={
                                                            goal?.Goals ??
                                                            0
                                                        }
                                                        disabled={
                                                            Boolean(
                                                                goal?.IsLocked
                                                            )
                                                        }
                                                        onChange={(
                                                            event
                                                        ) =>
                                                            handleProviderGoalChange(
                                                                program.ProgramType,
                                                                provider.Id,
                                                                event
                                                                    .target
                                                                    .value
                                                            )
                                                        }
                                                        slotProps={{
                                                            htmlInput:
                                                                {
                                                                    min: 0,
                                                                    style: {
                                                                        textAlign:
                                                                            'center',
                                                                    },
                                                                },
                                                        }}
                                                    />

                                                    {goal && (
                                                        <Tooltip
                                                            title={
                                                                goal.IsLocked
                                                                    ? 'Unlock allocation'
                                                                    : 'Lock allocation'
                                                            }
                                                        >
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    handleToggleLock(
                                                                        program.ProgramType,
                                                                        provider.Id
                                                                    )
                                                                }
                                                            >
                                                                {goal.IsLocked ? (
                                                                    <LockOutlined
                                                                        fontSize="small"
                                                                        color="primary"
                                                                    />
                                                                ) : (
                                                                    <LockOpenOutlined fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Grid>
                                            );
                                        }
                                    )}
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                )}
            </Box>

            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={() =>
                    setNotification((previous) => ({
                        ...previous,
                        open: false,
                    }))
                }
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Alert
                    severity={notification.severity}
                    variant="filled"
                    onClose={() =>
                        setNotification((previous) => ({
                            ...previous,
                            open: false,
                        }))
                    }
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default OnboardingGoals;